import {
  BookOpen,
  LayoutDashboard,
  Mail,
  FileText,
  FileStack,
  Files,
  Webhook,
  Users,
  Settings,
  Activity,
  CheckCircle,
  Server,
  Wallet,
  Database,
  Trash2,
  GraduationCap,
  Compass,
  Network,
  Building2,
  Plug,
} from 'lucide-react'
import DashboardGuide from './DashboardGuide'
import TemplatesGuide from './TemplatesGuide'
import UsersGuide from './UsersGuide'
import RequestsGuide from './RequestsGuide'
import DocumentsGuide from './DocumentsGuide'
import EmailQueueGuide from './EmailQueueGuide'
import WebhooksGuide from './WebhooksGuide'
import SettingsGuide from './SettingsGuide'
import ActivityGuide from './ActivityGuide'
import DataRetentionGuide from './DataRetentionGuide'
import GettingStartedGuide from './GettingStartedGuide'
import ApprovalsGuide from './ApprovalsGuide'
import OutlookAddinGuide from './OutlookAddinGuide'
import LearningGuide from './LearningGuide'
import OutputDestinationsGuide from './OutputDestinationsGuide'
import IntegrationsGuide from './IntegrationsGuide'
import BillingGuide from './BillingGuide'
import RecycleBinGuide from './RecycleBinGuide'
import OpCoWorkingGuide from './OpCoWorkingGuide'
import OpCoGuide from './OpCoGuide'

import {
  gettingStartedSections,
  approvalsSections,
  outlookSections,
  learningSections,
  outputSections,
  integrationsSections,
  billingSections,
  recycleSections,
  opcoWorkingSections,
  opcoSections,
  usersSections,
} from './guideSections'

const ALL = ['user', 'admin', 'super_admin']
const ADMINS = ['admin', 'super_admin']

// One entry per guide module, in the order of the app's sidebar.
//   audiences        which guide levels list it (see guideAudience.js)
//   feature          hidden when that flag is off ('opco', 'connectors', ...); a list means any of
//   userRoles        for the User level only: the stored roles that get it
//   hiddenForRoles   for the User level only: stored roles that do not
//   keywords         extra words search should match
// `sections` must match the numbered headings in the guide file, in order.
export const modules = [
  { id: 'getting-started', label: 'Getting Started', icon: Compass, Component: GettingStartedGuide, audiences: ALL, sections: gettingStartedSections, keywords: ['roles', 'sign in', 'sidebar', 'notifications'] },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, Component: DashboardGuide, audiences: ALL, sections: ['Page Header & Controls', 'KPI Stats Cards', 'Processing Trend & Status Distribution', 'Recently Processed Documents Table', 'Recent Activity, Quick Actions & System Health'] },
  { id: 'requests', label: 'Requests', icon: FileText, Component: RequestsGuide, audiences: ALL, sections: ['Page Header & New Request', 'Filtering & Search', 'Requests Table', 'Request Status Lifecycle', 'Creating a New Request', 'Request Detail - Split Panel View', 'Understanding AI Confidence', 'Processing Modal', 'Edit Mode & Audit Trail', 'Cancelling a Request', 'AI Operations & Cost Breakdown', 'Activity Log & Processing Log'], keywords: ['publish', 'send for review'] },
  { id: 'approvals', label: 'Approvals', icon: CheckCircle, Component: ApprovalsGuide, audiences: ALL, hiddenForRoles: ['sender'], sections: approvalsSections, keywords: ['approve', 'review', 'assign'] },
  { id: 'templates', label: 'Templates', icon: FileStack, Component: TemplatesGuide, audiences: ALL, userRoles: ['template_manager'], sections: ['Templates List Page', 'Template Cards', 'Creating a New Template', 'Template Detail Page (Managing Fields)', 'Template Webhooks', 'Settings Tab', 'Manage Access', 'AI Field Suggestions', 'Complete Workflow: Template to Extraction'], keywords: ['fields', 'analyzer'] },
  { id: 'email-queue', label: 'Email Queue', icon: Mail, Component: EmailQueueGuide, audiences: ALL, sections: ['Page Header & Actions', 'Stats Cards', 'Status Tabs', 'Search & Time Range Filter', 'Emails Table', 'Ingest Email Modal', 'Duplicate Detection', 'Email Detail Page'] },
  { id: 'documents', label: 'Documents', icon: Files, Component: DocumentsGuide, audiences: ALL, sections: ['Page Header', 'Filtering & Search', 'Documents Table', 'Document Detail Page', 'Available Actions'] },
  { id: 'outlook-addin', label: 'Outlook Add-in', icon: Plug, Component: OutlookAddinGuide, audiences: ALL, sections: outlookSections, keywords: ['plugin', 'outlook'] },
  { id: 'activity', label: 'Activity', icon: Activity, Component: ActivityGuide, audiences: ADMINS, sections: ['Page Header', 'Search & Type Filter', 'Activity List', 'Pagination'] },
  { id: 'webhooks', label: 'Connectors', icon: Webhook, Component: WebhooksGuide, audiences: ADMINS, feature: 'connectors', sections: ['Page Header & Add Webhook', 'Webhooks List Table', 'Create / Edit Webhook Form', 'Webhook Detail Panel', 'Active / Disabled Toggle', 'Delivery Logs Table', 'Expanded Delivery Detail', 'Delete Confirmation Modal', 'Empty State (First Run)'], keywords: ['webhook', 'connector', 'payload mapping'] },
  { id: 'output-destinations', label: 'Output Destinations', icon: Server, Component: OutputDestinationsGuide, audiences: ADMINS, feature: 'connectors', sections: outputSections, keywords: ['sftp', 'ftp', 'csv', 'azure sql'] },
  { id: 'integrations', label: 'Business Central & NetSuite', icon: Network, Component: IntegrationsGuide, audiences: ADMINS, feature: ['business_central', 'netsuite'], sections: integrationsSections, keywords: ['erp', 'purchase order', 'post'] },
  { id: 'billing', label: 'Billing & Analytics', icon: Wallet, Component: BillingGuide, audiences: ADMINS, sections: billingSections, keywords: ['cost', 'pages', 'tokens', 'plan'] },
  { id: 'users', label: 'Users & Groups', icon: Users, Component: UsersGuide, audiences: ADMINS, sections: usersSections, keywords: ['roles', 'invite', 'groups', 'approver', 'reviewer'] },
  { id: 'opco', label: 'OpCos (Super Admin)', icon: Building2, Component: OpCoGuide, audiences: ['super_admin'], feature: 'opco', sections: opcoSections, keywords: ['super admin', 'workspace', 'auto-assign', 'move work', 'unassigned', 'members'] },
  { id: 'settings', label: 'Settings', icon: Settings, Component: SettingsGuide, audiences: ADMINS, sections: ['Page Header & Tabs', 'Identity & Brand Colour', 'Quick-Pick Colour Palette', 'Images (Logo & Favicon)', 'Outlook Plugin Branding', 'Live Preview Panel', 'Data Display Preferences', 'Data Display Live Preview'] },
  { id: 'data-retention', label: 'Data Retention', icon: Database, Component: DataRetentionGuide, audiences: ADMINS, sections: ['Page Header & Tabs', 'Understanding Data Types', 'Configuring a Policy', 'Preview Before Enabling', 'How Deletion Works', 'Deletion Log (Audit Trail)', 'Best Practices'] },
  { id: 'recycle-bin', label: 'Recycle Bin', icon: Trash2, Component: RecycleBinGuide, audiences: ADMINS, sections: recycleSections, keywords: ['restore', 'deleted'] },
  { id: 'opco-working', label: 'Working in an OpCo', icon: Building2, Component: OpCoWorkingGuide, audiences: ['user', 'admin'], feature: 'opco', sections: opcoWorkingSections, keywords: ['workspace', 'switch', 'unassigned', 'manager', 'member'] },
  { id: 'learning', label: 'Learning', icon: GraduationCap, Component: LearningGuide, audiences: ALL, sections: learningSections, keywords: ['video', 'how-to'] },
]

export const FALLBACK_ICON = BookOpen
